import React, { memo, useCallback, useMemo } from "react";
// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";

// types
import { StealDealItemProps } from "../types";

// styles
import {
    ImageContainer,
    StyledImage,
    ItemDetailsContainer,
    BasePriceContainer,
    ButtonContainer,
    FlexContainer,
    ItemContainer,
    AddButton,
    PriceTag,
    Price,
} from "./StealDealItem.styles";

// constants
import { TEXTS } from "../constants";
import { STEAL_DEAL_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { CROSS_SELL_M2_ITEM_PLACEHOLDER } from "@cart-ui/constants";

// helpers
import { priceInRupee } from "@cart-ui/helpers/Price";
import { getStealDealItemImageSize } from "./config";

function StealDealItem({
    item,
    isUnlockedState,
    onAddStealDeal,
    onAddStealDealEvent,
    isSnaccStorefrontV2,
}: StealDealItemProps): React.ReactElement {
    const itemPrice = item?.subtotal ?? item?.base_price ?? 0;
    const finalPrice = item?.final_price ?? 0;
    const handleAddItem = useCallback(() => {
        onAddStealDeal(item);
        //analytics event for add steal deal item
        onAddStealDealEvent?.(item);
    }, [item, onAddStealDeal, onAddStealDealEvent]);
    const stealDealItemImageSize = useMemo(() => getStealDealItemImageSize(isSnaccStorefrontV2), [isSnaccStorefrontV2]);
    const stealDealItemCloudinaryHeight = useMemo(
        () => stealDealItemImageSize.height * 2,
        [stealDealItemImageSize.height],
    );

    return (
        <ItemContainer>
            <div>
                <ImageContainer isSnaccStorefrontV2={isSnaccStorefrontV2}>
                    <StyledImage
                        cloudinaryId={item?.cloudinaryImageId || ""}
                        errorFallbackSrc={CROSS_SELL_M2_ITEM_PLACEHOLDER}
                        fit="cover"
                        cloudinaryHeight={stealDealItemCloudinaryHeight}
                        isSnaccStorefrontV2={isSnaccStorefrontV2}
                    />
                </ImageContainer>
            </div>

            <ItemDetailsContainer>
                <Text
                    variant="V2.body2.neutral.medium"
                    color="text_Highest_Emphasis"
                    maxLines={2}
                    data-testid={STEAL_DEAL_WIDGET_TEST_IDS.ITEM_NAME}
                >
                    <FoodSymbolIconSvg vegType={item.isVeg} /> <span>{item?.name}</span>
                </Text>

                <FlexContainer>
                    {itemPrice !== finalPrice && (
                        <BasePriceContainer
                            variant="V2.body2.neutral.medium"
                            color="text_Low_Emphasis"
                            data-testid={STEAL_DEAL_WIDGET_TEST_IDS.ITEM_ORIGINAL_PRICE}
                        >
                            {`₹${priceInRupee(itemPrice)}`}
                        </BasePriceContainer>
                    )}
                    <PriceTag>
                        <Price variant="V2.body2.neutral.bold" color="text_Highest_Emphasis">
                            {`₹${priceInRupee(finalPrice)}`}
                        </Price>
                    </PriceTag>
                </FlexContainer>
            </ItemDetailsContainer>

            <ButtonContainer>
                <AddButton
                    variant="primary"
                    isDisabled={!isUnlockedState}
                    onClick={handleAddItem}
                    data-testid={STEAL_DEAL_WIDGET_TEST_IDS.CLAIM_BUTTON}
                >
                    <Text
                        variant="V2.body2.neutral.bold"
                        color={!isUnlockedState ? "text_Button_Disabled" : "text_Highest_Emphasis"}
                        data-testid={STEAL_DEAL_WIDGET_TEST_IDS.CLAIM_BUTTON_TEXT}
                    >
                        {TEXTS.ADD}
                    </Text>
                </AddButton>
            </ButtonContainer>
        </ItemContainer>
    );
}

export default memo(StealDealItem);
